import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Typography,
  Box,
  InputAdornment,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  Close as CloseIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { IconButton } from '@mui/material';
import { getUsersByRole } from '../../services/userService';
import { assignManager } from '../../services/assignmentService';
import { ROLE_COLORS } from '../../config/constants';

const AssignManagerDialog = ({ open, onClose, studentId, onAssigned }) => {
  const [managers, setManagers] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [notes, setNotes] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      fetchManagers();
      setSelectedId(null);
      setNotes('');
      setSearch('');
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const fetchManagers = async () => {
    setLoading(true);
    try {
      const response = await getUsersByRole('manager');
      setManagers(response.data?.data?.users || []);
    } catch (err) {
      setError('Failed to load managers.');
      console.error('Error fetching managers:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredManagers = managers.filter((m) =>
    (m.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (m.email || '').toLowerCase().includes(search.toLowerCase())
  );

  const handleAssign = async () => {
    if (!selectedId) return;
    setSubmitting(true);
    setError(null);
    try {
      await assignManager(studentId, selectedId, notes);
      if (onAssigned) onAssigned();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to assign manager.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="assign-manager-dialog-title"
    >
      <DialogTitle
        id="assign-manager-dialog-title"
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
      >
        Assign Manager
        <IconButton size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <TextField
          fullWidth
          size="small"
          placeholder="Search managers..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 2 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : filteredManagers.length === 0 ? (
          <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 3 }}>
            No managers found.
          </Typography>
        ) : (
          <List
            dense
            sx={{
              maxHeight: 300,
              overflow: 'auto',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
            }}
          >
            {filteredManagers.map((manager) => (
              <ListItem
                key={manager._id}
                onClick={() => setSelectedId(manager._id)}
                selected={selectedId === manager._id}
                sx={{
                  cursor: 'pointer',
                  '&.Mui-selected': {
                    backgroundColor: 'primary.light',
                    '&:hover': { backgroundColor: 'primary.light' },
                  },
                }}
              >
                <ListItemAvatar>
                  <Avatar
                    sx={{
                      bgcolor: ROLE_COLORS.manager,
                      width: 36,
                      height: 36,
                      fontSize: '0.875rem',
                    }}
                  >
                    {manager.name?.charAt(0)?.toUpperCase() || 'M'}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={manager.name}
                  secondary={manager.email}
                />
                {selectedId === manager._id && (
                  <CheckIcon color="primary" fontSize="small" />
                )}
              </ListItem>
            ))}
          </List>
        )}

        <TextField
          fullWidth
          multiline
          rows={2}
          label="Notes (optional)"
          placeholder="Reason for assignment..."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          sx={{ mt: 2 }}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleAssign}
          disabled={!selectedId || submitting}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {submitting ? 'Assigning...' : 'Assign Manager'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignManagerDialog;
